import { useSignalREvent } from '../hooks/useSignalREvent';
import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import axiosInstance from '../api/axiosInstance';
import { Clock, CheckCircle, XCircle, AlertCircle, Search, Volume2, VolumeX, ChevronDown, /*Play*/ } from 'lucide-react';
import { updateOrderStatus } from '../api/orderApi';
import { statusOptions, getStatusInfo, getPaymentStatusInfo } from '../utils/session';
import { showGlobalToast } from '../utils/toast';
// import { playRingtone, getRingtonesByCategory } from '../utils/ringtonePlayer'; 

// Get restaurant GUID from local storage, with a fallback
let RESTAURANT_GUID = localStorage.getItem('restaurantGuid') || '';
const API_BASE = process.env.REACT_APP_API_BASE || 'https://localhost:44381';

// Define the types for better code clarity and type safety
interface OrderItem {
  orderItemId: number;
  orderId: number;
  menuItemId: number;
  menuItemName: string;
  quantity: number;
  price: number;
  discountedPrice?: number;
  cookingInstructions: string;
  status: string;
}

interface Order {
  orderId: number;
  tableLabel: string;
  orderStatus: string;
  totalAmount: number;
  finalAmount: number;
  placedAt: string;
  estimatedTime?: string;
  items: OrderItem[];
  isPaid: boolean; // Added isPaid flag
}

interface LiveOrderProps {
  isDarkTheme?: boolean;
}

// Modal component for detailed order view and editing
const OrderDetailsModal: React.FC<{
  order: Order;
  onClose: () => void;
  onItemStatusChange: (orderItemId: number, orderId: number, status: string) => void;
  isDarkTheme: boolean;
}> = ({ order, onClose, onItemStatusChange, isDarkTheme }) => {
  const { orderStatus, placedAt, isPaid } = order;
  const orderStatusInfo = getStatusInfo(orderStatus);
  const modalRef = useRef<HTMLDivElement>(null);

  // State to track which item's dropdown is open and its direction
  const [editingItemId, setEditingItemId] = useState<number | null>(null);
  const [dropdownDirection, setDropdownDirection] = useState<'up' | 'down'>('down');
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close the dropdown when clicking outside of it
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setEditingItemId(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleStatusSelect = (orderItemId: number, orderId: number, newStatus: string) => {
    onItemStatusChange(orderItemId, orderId, newStatus);
    setEditingItemId(null);
  };

  const handleDropdownToggle = (event: React.MouseEvent<HTMLButtonElement>, itemId: number) => {
    // If the same dropdown is clicked, close it.
    if (editingItemId === itemId) {
      setEditingItemId(null);
      return;
    }

    // Determine the direction to open the dropdown
    const buttonRect = event.currentTarget.getBoundingClientRect();
    const modalRect = modalRef.current?.getBoundingClientRect();

    if (modalRect) {
      const dropdownHeight = statusOptions.length * 40; // Estimate 40px per item
      const spaceBelow = modalRect.bottom - buttonRect.bottom;

      if (spaceBelow < dropdownHeight) {
        setDropdownDirection('up');
      } else {
        setDropdownDirection('down');
      }
    }

    setEditingItemId(itemId);
  };
  const paymentStatusInfo = getPaymentStatusInfo(isPaid);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-75">
      <div ref={modalRef} className={`relative w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6 rounded-xl shadow-2xl ${isDarkTheme ? 'bg-gray-800 text-gray-100' : 'bg-white text-gray-900'}`}>
        <button onClick={onClose} className="absolute top-4 right-4 text-gray-400 hover:text-gray-200 transition-colors">
          <XCircle size={24} />
        </button>
        <h3 className={`text-2xl font-bold mb-2 ${isDarkTheme ? 'text-gray-100' : 'text-gray-800'}`}>Order Details #{order.orderId}</h3>
        <p className={`text-sm mt-1 ${isDarkTheme ? 'text-gray-400' : 'text-gray-500'}`}>Table: <span className="font-semibold">{order.tableLabel}</span></p>
        <p className={`text-sm ${isDarkTheme ? 'text-gray-400' : 'text-gray-500'}`}>Placed at: <span className="font-semibold">{new Date(placedAt).toLocaleTimeString()}</span></p>

        <div className={`mt-4 mb-6 pt-4 border-t ${isDarkTheme ? 'border-gray-600' : 'border-gray-200'}`}>
          <div className="flex justify-between items-center text-sm font-medium">
            <div className="flex flex-col space-y-2">
              <div className="flex items-center space-x-2">
                <span className={`${isDarkTheme ? 'text-gray-400' : 'text-gray-500'}`}>Current Status:</span>
                <orderStatusInfo.icon size={16} className={`${orderStatusInfo.color}`} />
                <span className={`${orderStatusInfo.color} font-bold`}>{orderStatusInfo.text}</span>
              </div>
              <div className="flex items-center space-x-2">
                <span className={`${isDarkTheme ? 'text-gray-400' : 'text-gray-500'}`}>Payment Status:</span>
                <paymentStatusInfo.icon size={16} className={`${paymentStatusInfo.color}`} />
                <span className={`${paymentStatusInfo.color} font-bold`}>{paymentStatusInfo.text}</span>
              </div>
            </div>
            <div className="text-right">
              <span className={`text-sm font-semibold ${isDarkTheme ? 'text-gray-400' : 'text-gray-500'}`}>Total Amount</span>
              <p className="text-xl font-extrabold text-orange-500 mt-1">
                {order.finalAmount && order.finalAmount < order.totalAmount ? (
                  <>
                    <span className="text-sm font-normal line-through text-gray-400 mr-2">₹{order.totalAmount.toFixed(2)}</span>
                    ₹{order.finalAmount.toFixed(2)}
                  </>
                ) : (
                  `₹${order.totalAmount.toFixed(2)}`
                )}
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <h4 className="text-lg font-bold">Items</h4>
          {order.items.map(item => {
            const itemStatusInfo = getStatusInfo(item.status);
            const isDropdownOpen = editingItemId === item.orderItemId;
            const isDropdownUp = isDropdownOpen && dropdownDirection === 'up';

            return (
              <div key={item.orderItemId} className={`p-4 rounded-lg flex items-center justify-between shadow-sm ${isDarkTheme ? 'bg-gray-700' : 'bg-gray-100'}`}>
                <div className="flex-grow">
                  <p className={`text-sm font-semibold ${isDarkTheme ? 'text-gray-200' : 'text-gray-800'}`}>
                    {item.menuItemName} X {item.quantity}
                    {item.discountedPrice && item.discountedPrice < item.price && (
                      <span className="ml-2 font-normal text-xs text-gray-400">
                        (
                        <span className="line-through">₹{item.price.toFixed(2)}</span>
                        {' '}
                        <span className="font-semibold text-orange-500">₹{item.discountedPrice.toFixed(2)}</span>
                        )
                      </span>
                    )}
                  </p>
                  <p className={`text-xs italic mt-1 ${isDarkTheme ? 'text-gray-400' : 'text-gray-500'}`}>{item.cookingInstructions || 'No instructions'}</p>
                </div>
                {/* Dropdown with consistent width */}
                <div className="relative flex flex-col items-end w-40" ref={dropdownRef}>
                  <button
                    onClick={(e) => handleDropdownToggle(e, item.orderItemId)}
                    className={`flex items-center justify-end w-full space-x-2 px-3 py-1.5 rounded-lg border-2 shadow-sm transition-colors duration-200 ${isDarkTheme ? 'bg-gray-700 text-gray-100 border-gray-600 hover:bg-gray-600' : 'bg-white text-gray-900 border-gray-300 hover:bg-gray-50'} focus:outline-none focus:ring-2 focus:ring-orange-500/50`}
                  >
                    <itemStatusInfo.icon size={16} className={`${itemStatusInfo.color}`} />
                    <span className={`text-sm font-semibold truncate ${itemStatusInfo.color}`}>{itemStatusInfo.text}</span>
                    <ChevronDown size={16} className={`transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''} ${isDarkTheme ? 'text-gray-400' : 'text-gray-600'}`} />
                  </button>
                  {isDropdownOpen && (
                    <ul className={`absolute z-10 w-full overflow-hidden rounded-lg shadow-xl border ${isDropdownUp ? 'bottom-full mb-2' : 'top-full mt-2'} ${isDarkTheme ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-200'}`}>
                      {statusOptions.map(status => {
                        const isCurrentStatus = status === item.status;
                        return (
                          <li
                            key={status}
                            onMouseDown={(e) => {
                              e.preventDefault();   // ✅ avoid losing focus
                              e.stopPropagation();
                              if (!isCurrentStatus) {
                                handleStatusSelect(item.orderItemId, order.orderId, status);
                              }
                            }}
                            className={`flex items-center space-x-2 p-3 text-sm cursor-pointer transition-colors
                              ${isCurrentStatus
                                ? `${isDarkTheme ? 'bg-gray-600 text-gray-400 cursor-not-allowed' : 'bg-gray-200 text-gray-500 cursor-not-allowed'}`
                                : `${isDarkTheme ? 'text-gray-100 hover:bg-orange-500 hover:text-white' : 'text-gray-800 hover:bg-orange-500 hover:text-white'}`}
                          `}
                          >
                            {status}
                          </li>

                        );
                      })}
                    </ul>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// Main component
const LiveOrderTracking: React.FC<LiveOrderProps> = ({ isDarkTheme = false }) => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [isMuted, setIsMuted] = useState(() => {
    const stored = localStorage.getItem('IsSoundMuted');
    return stored === 'true';
  });

  const toggleMute = () => setIsMuted(prev => {
    const next = !prev;
    localStorage.setItem('IsSoundMuted', String(next));
    return next;
  });

  const [showOrderDetailsModal, setShowOrderDetailsModal] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState<number | null>(null);
  const ordersRef = useRef<Order[]>([]);

  useEffect(() => {
    ordersRef.current = orders;
  }, [orders]);

  // const ringtones = getRingtonesByCategory('New Order');

  // const [selectedRingtone, setSelectedRingtone] = useState('new_order_01'); // default
  // // UseCallback to play new order sound
  // const playNewOrderSound = useCallback((ringtoneKey: string) => {
  //   if (isMuted) return;
  //   playRingtone(ringtoneKey);
  // }, [isMuted]);

  // Use useCallback to memoize the fetch function, improving performance
  const fetchOrders = useCallback(async (isInitial = false) => {
    if (isInitial) setLoading(true);
    if (!RESTAURANT_GUID) RESTAURANT_GUID = localStorage.getItem('restaurantGuid') || '';
    if (!RESTAURANT_GUID) {
      setLoading(false);
      return;
    }
    try {
      const res = await axiosInstance.get(`/order/restaurant/${RESTAURANT_GUID}`);
      const fetched = Array.isArray(res.data)
        ? res.data.sort((a: Order, b: Order) => new Date(b.placedAt).getTime() - new Date(a.placedAt).getTime())
        : [];

      const currentOrders = ordersRef.current;
      const oldOrdersMap = new Map(currentOrders.map(o => [o.orderId, o]));
      let hasChanges = false;
      const updatedOrders = fetched.map(newOrder => {
        const oldOrder = oldOrdersMap.get(newOrder.orderId);
        if (!oldOrder || JSON.stringify(oldOrder) !== JSON.stringify(newOrder)) {
          hasChanges = true;
          return newOrder;
        }
        return oldOrder;
      });

      if (hasChanges || fetched.length !== currentOrders.length) {
        setOrders(updatedOrders);
      }

    } catch (err) {
      showGlobalToast('Oops! Something went wrong. Please try again.', 'error');
      console.error(err);
    } finally {
      if (isInitial) setLoading(false);
    }
  }, []); // [playNewOrderSound, selectedRingtone]);

  const handleLiveOrderStatus = (update: {
    orderId: number;
    orderStatus: string;
    isUpdateAllItems?: boolean;
    items?: { orderItemId: number; status: string }[];
  }) => {
    setOrders(prev =>
      prev.map(order => {
        if (order.orderId !== update.orderId) return order;

        // Update items
        const updatedItems = order.items?.map(item => {
          if (update.isUpdateAllItems) return { ...item, status: update.orderStatus };
          const matchedItem = update.items?.find(i => i.orderItemId === item.orderItemId);
          return matchedItem ? { ...item, status: matchedItem.status } : item;
        });

        return { ...order, orderStatus: update.orderStatus, items: updatedItems };
      })
    );

    console.log('Live order status updated via SignalR:', update);
  };

  // Use useEffect to manage the polling interval
  // Initial fetch only
  useEffect(() => {
    fetchOrders(true);
  }, [fetchOrders]);

  // Subscribe to SignalR event for real-time updates
  useSignalREvent('LiveOrderUpdate', 'liveOrderTracking', () => fetchOrders(false));
  useSignalREvent('UpdateOrderStatus', 'liveOrderTracking', (data) => {
    handleLiveOrderStatus(data);
  });


  const handleItemStatusChange = useCallback(
    async (orderItemId: number, orderId: number, status: string) => {
      try {
        await axiosInstance.put(`${API_BASE}/api/order/UpdateOrderItemStatus/${orderId}/${orderItemId}/${status}`);
        showGlobalToast(`Item status updated to "${status}"!`, 'success'); // Re-added toast call
        //fetchOrders(false);
      } catch {
        showGlobalToast('Failed to update item status.', 'error'); // Re-added toast call
      }
    },
    [/*fetchOrders*/]
  );

  const handleAcceptOrder = useCallback(async (orderId: number) => {
    try {
      await updateOrderStatus(orderId.toString(), 'Accepted'); // Ensure the API call is made
      setOrders(prevOrders =>
        prevOrders.map(order =>
          order.orderId === orderId
            ? {
              ...order,
              orderStatus: "Accepted",
              items: order.items?.map(item => ({
                ...item,
                status: "Accepted",
              })),
            }
            : order
        )
      );
      showGlobalToast(`Order #${orderId} has been accepted!`, 'success'); // Re-added toast call
      //fetchOrders(false);
    } catch (err) {
      console.error(err);
      showGlobalToast('Failed to accept the order.', 'error'); // Re-added toast call
    }
  }, [/*fetchOrders*/]);


  const getOrderStatusDisplay = (status: string, placedAt: string) => {
    // Special case for 'Placed' orders that have been waiting for more than 5 minutes
    if (status === 'Placed') {
      const placedDate = new Date(placedAt);
      const now = new Date();
      const minutesDiff = (now.getTime() - placedDate.getTime()) / (1000 * 60);

      if (minutesDiff > 5) {
        return { text: 'Waiting to accept', color: 'text-orange-500', icon: AlertCircle };
      }
    }
    // Return standard status info for all other statuses
    return getStatusInfo(status);
  };

  // Use useMemo to cache categorized orders and avoid recalculations on every render
  const categorizedOrders = useMemo(() => {
    const newOrders = orders.filter(o => o.orderStatus === 'Placed');
    const inProgress = orders.filter(o => ['Accepted', 'Preparing'].includes(o.orderStatus));
    const ready = orders.filter(o => o.orderStatus === 'Ready');
    const delivered = orders.filter(o => o.orderStatus === 'Delivered');
    const cancelled = orders.filter(o => o.orderStatus === 'Cancelled');
    return { newOrders, inProgress, ready, delivered, cancelled };
  }, [orders]);

  const [activeTab, setActiveTab] = useState<'new' | 'progress' | 'ready' | 'delivered' | 'cancelled'>('new');


  // Filter orders based on the search query, using useMemo for efficiency
  const filteredOrders = useMemo(() => {
    const tabMap = {
      new: categorizedOrders.newOrders,
      progress: categorizedOrders.inProgress,
      ready: categorizedOrders.ready,
      delivered: categorizedOrders.delivered,
      cancelled: categorizedOrders.cancelled,
    };

    // Only filter for the visible statuses. Do not show Completed or Paid
    const visibleStatuses = ['Placed', 'Accepted', 'Preparing', 'Ready', 'Delivered', 'Cancelled'];
    const ordersToFilter = orders.filter(o => visibleStatuses.includes(o.orderStatus));

    const lowerCaseQuery = searchQuery.toLowerCase();

    // If a search query is present, filter all visible orders.
    // Otherwise, filter by the active tab.
    if (searchQuery) {
      return ordersToFilter.filter((order: Order) =>
        order.orderId.toString().includes(lowerCaseQuery) ||
        order.tableLabel.toLowerCase().includes(lowerCaseQuery) ||
        order.items.some((item: OrderItem) =>
          item.menuItemName.toLowerCase().includes(lowerCaseQuery) ||
          item.cookingInstructions.toLowerCase().includes(lowerCaseQuery)
        )
      );
    } else {
      return tabMap[activeTab];
    }
  }, [orders, searchQuery, activeTab, categorizedOrders]);

  const openOrderDetails = (order: Order) => {
    setSelectedOrderId(order.orderId);
    setShowOrderDetailsModal(true);
  };

  const closeOrderDetails = () => {
    setShowOrderDetailsModal(false);
    setSelectedOrderId(null);
  };

  // const handleRingtoneChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
  //   setSelectedRingtone(event.target.value);
  // };

  // const handlePlayPreview = () => {
  //   playNewOrderSound(selectedRingtone);
  // };

  const tabs = [
    { id: 'new', label: 'New Orders', count: categorizedOrders.newOrders.length },
    { id: 'progress', label: 'In Progress', count: categorizedOrders.inProgress.length },
    { id: 'ready', label: 'Ready', count: categorizedOrders.ready.length },
    { id: 'delivered', label: 'Delivered', count: categorizedOrders.delivered.length },
    { id: 'cancelled', label: 'Cancelled', count: categorizedOrders.cancelled.length },
  ];

  return (
    <div
      className={`overflow-y-auto transition-colors duration-300 ${isDarkTheme
        ? 'bg-gray-900 text-gray-100'
        : 'bg-gray-100 text-gray-900'
        }`}
    >
      <header className={`sticky top-0 z-10 flex justify-between items-center px-4 pt-2 pb-2 ${isDarkTheme ? 'bg-gray-900' : 'bg-gray-100'}`}>
        <h1 className="text-3xl font-extrabold text-orange-500">Live Order Tracking</h1>
        <div className="flex items-center space-x-4">
          {/* <div className="relative">
            <select
              value={selectedRingtone}
              onChange={handleRingtoneChange}
              className={`p-2 pr-8 rounded-lg border-2 appearance-none cursor-pointer transition-colors ${isDarkTheme ? 'bg-gray-800 text-gray-200 border-gray-700 hover:bg-gray-700' : 'bg-white text-gray-800 border-gray-300 hover:bg-gray-100'}`}
            >
              {ringtones.map(ringtone => (
                <option key={ringtone.key} value={ringtone.key}>
                  {ringtone.displayName}
                </option>
              ))}
            </select>
            <ChevronDown size={16} className={`absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none ${isDarkTheme ? 'text-gray-400' : 'text-gray-600'}`} />
          </div> */}
          {/* <button
            onClick={handlePlayPreview}
            className={`p-2 rounded-full transition-colors ${isDarkTheme ? 'bg-gray-800 hover:bg-gray-700' : 'bg-gray-200 hover:bg-gray-300'}`}
            title="Play preview"
          >
            <Play size={24} className="text-orange-500" />
          </button> */}
          <button
            onClick={toggleMute}
            className={`p-2 rounded-full transition-colors ${isDarkTheme ? 'bg-gray-800 hover:bg-gray-700' : 'bg-gray-200 hover:bg-gray-300'}`}
            title={isMuted ? 'Unmute alerts' : 'Mute alerts'}
          >
            {isMuted ? <VolumeX size={24} className="text-red-500" /> : <Volume2 size={24} className="text-green-500" />}
          </button>
        </div>
      </header>

      {/* Universal Search Bar */}
      <div className="relative mb-3 px-4">
        <div className={`flex items-center rounded-lg shadow-sm border overflow-hidden ${isDarkTheme ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-300'}`}>
          <Search className={`w-5 h-5 ml-3 ${isDarkTheme ? 'text-gray-400' : 'text-gray-500'}`} />
          <input
            type="text"
            placeholder="Search by Order ID, Table, or Item..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className={`flex-1 p-2 outline-none ${isDarkTheme ? 'bg-gray-800 text-gray-100 placeholder-gray-400' : 'bg-white text-gray-900 placeholder-gray-500'}`}
          />
        </div>
      </div>

      <div className={`p-2 rounded-xl shadow-md flex justify-between mb-3 mx-4 ${isDarkTheme ? 'bg-gray-800' : 'bg-gray-200'}`}>
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => { setActiveTab(tab.id as 'new' | 'progress' | 'ready' | 'delivered' | 'cancelled'); setSearchQuery(''); }}
            className={`flex-1 p-3 text-center text-sm font-semibold rounded-lg transition-colors ${activeTab === tab.id ? 'bg-orange-500 text-white' : isDarkTheme ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-600 hover:bg-gray-300'}`}
          >
            <span className="hidden sm:inline">{tab.label}</span> ({tab.count})
          </button>
        ))}
      </div>

      <main className="w-full h-full px-4 pb-4">
        {loading ? (
          <div className="text-center text-gray-400 text-lg p-20">Loading orders...</div>
        ) : filteredOrders.length > 0 ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
            {filteredOrders.map(order => {
              const orderStatusInfo = getOrderStatusDisplay(order.orderStatus, order.placedAt);
              return (
                <div
                  key={order.orderId}
                  className={`p-4 rounded-xl shadow-inner transition-all duration-300 transform hover:-translate-y-1 cursor-pointer relative ${isDarkTheme ? 'bg-gray-800 hover:shadow-orange-500/20' : 'bg-white hover:shadow-orange-200/50'}`}
                  onClick={() => openOrderDetails(order)}
                >
                  <div className={`flex justify-between items-start mb-2`}>
                    <div>
                      <h4 className={`text-lg font-bold ${isDarkTheme ? 'text-gray-100' : 'text-gray-800'}`}>Order #{order.orderId}</h4>
                      <p className={`text-xs mt-1 ${isDarkTheme ? 'text-gray-400' : 'text-gray-500'}`}>Table: <span className={`font-semibold ${isDarkTheme ? 'text-gray-200' : 'text-gray-700'}`}>{order.tableLabel}</span></p>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-extrabold text-orange-500">
                        {order.finalAmount && order.finalAmount < order.totalAmount ? (
                          <>
                            <span className="text-sm font-normal line-through text-gray-400 mr-2">₹{order.totalAmount.toFixed(2)}</span>
                            ₹{order.finalAmount.toFixed(2)}
                          </>
                        ) : (
                          `₹${order.totalAmount.toFixed(2)}`
                        )}
                      </p>
                      <div className="flex items-center justify-end mt-1 space-x-1">
                        <orderStatusInfo.icon size={12} className={`${orderStatusInfo.color}`} />
                        <span className={`text-xs font-semibold ${orderStatusInfo.color}`}>{orderStatusInfo.text}</span>
                      </div>
                    </div>
                  </div>

                  <div className={`flex justify-between items-center text-xs font-medium pt-2 border-t ${isDarkTheme ? 'border-gray-600' : 'border-gray-200'}`}>
                    <span className={`${isDarkTheme ? 'text-gray-400' : 'text-gray-500'}`}>{order.items.length} items</span>
                    <span className={`${isDarkTheme ? 'text-gray-400' : 'text-gray-500'}`}>Placed at: {new Date(order.placedAt).toLocaleTimeString()}</span>
                  </div>

                  {/* Payment Status Message for Delivered Orders */}
                  {(order.orderStatus === 'Delivered') && (
                    <div className="mt-2 text-sm flex items-center space-x-2">
                      {order.isPaid ? (
                        <div className="flex items-center space-x-1 text-green-500 font-bold">
                          <CheckCircle size={14} />
                          <span>Paid</span>
                        </div>
                      ) : (
                        <div className="flex items-center space-x-1 text-orange-500 font-bold">
                          <Clock size={14} />
                          <span>Payment Pending</span>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Order-level action buttons - only for placed orders */}
                  {(order.orderStatus === 'Placed' && activeTab !== 'cancelled') && (
                    <div className="mt-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleAcceptOrder(order.orderId);
                        }}
                        className="w-full bg-orange-500 text-white text-sm p-1.5 rounded-lg font-semibold hover:bg-orange-600 transition-colors"
                      >
                        Accept Order
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center text-gray-400 text-lg p-20">
            {searchQuery ? `No orders found for "${searchQuery}".` : `No orders to display in this category.`}
          </div>
        )}
      </main>

      {showOrderDetailsModal && selectedOrderId !== null &&
        orders.find(o => o.orderId === selectedOrderId) && (
          <OrderDetailsModal
            order={orders.find(o => o.orderId === selectedOrderId)!}
            onClose={closeOrderDetails}
            onItemStatusChange={handleItemStatusChange}
            isDarkTheme={isDarkTheme}
          />
        )}
    </div>
  );
};

export default LiveOrderTracking;
